// src/api/paymentHandler.ts
import { ethers, providers } from 'ethers';
import { signMessage } from '@/utils';
import { createERC20Controller, getContractAddresses } from '@/services/contracts-controller';
import { WalletContext } from '@/services';

// ==========================
// 🪙 类型定义
// ==========================
export interface PaymentInfo {
    scheme: 'local' | 'ethereum' | string;
    network?: string;
    token?: string;
    destination?: string;
    amount?: string;
    expires_at?: string;
    ticketSource?: string;
}

export type PaymentEvents = {
    start: PaymentInfo;
    success: PaymentInfo;
    fail: { payment: PaymentInfo; error: Error | string };
};
// ==========================
// 🔧 工具函数：构建 X-PAYMENT header
// ==========================
async function buildPaymentHeader(payment: PaymentInfo, signedData: string): Promise<string> {
    const payload: Record<string, any> = {
        scheme: payment.scheme,
        network: payment.network,
        token: payment.token,
        destination: payment.destination,
        amount: payment.amount,
        payer: WalletContext.Address,
        timestamp: new Date().toISOString(),
    };
    // 对于 bsc 策略，signedData 是 rawTxData（序列化的交易数据）
    // 对于 local 策略，signedData 是签名消息
    payload.signature = signedData;
    // 返回 Base64 编码字符串
    return btoa(JSON.stringify(payload));
}
// ==========================
// ⚙️ 支付策略模式
// ==========================
const PaymentStrategies: Record<string, (payment: PaymentInfo) => Promise<string>> = {
    /** 🧾 本地扣费 */
    local: async (payment) => {
        console.log('[Payment] Local payment start...');
        const messageToSign = `${payment.amount || '0'},${payment.token || 'tokens'},${payment.destination || ''}`;
        console.log('[Payment] Message to sign:', messageToSign);
        try {
            const signature = await signMessage(messageToSign);
            console.log('[Payment] Message signature:', signature);
            return signature;
        } catch (error) {
            console.error('[Payment] Sign message failed:', error);
            throw new Error(`Failed to sign message: ${error instanceof Error ? error.message : String(error)}`);
        }
    },

    /** ⛓️ ERC20 转账支付 - 前端签名并打包交易数据 */
    bsc: async (payment) => {
        console.log('[Payment] ERC20 transfer payment start (sign only)...');

        // 验证必需参数
        if (!payment.token) {
            throw new Error('token address is required for ERC20 payment');
        }
        if (!payment.destination) {
            throw new Error('destination address is required for ERC20 payment');
        }
        if (!payment.amount) {
            throw new Error('amount is required for ERC20 payment');
        }

        // 检查钱包连接
        if (!WalletContext.Address || !WalletContext.Provider) {
            throw new Error('Wallet not connected');
        }
        const tokenAddress = getContractAddresses([payment.token])[0];
        console.log('[Payment] Token address:', tokenAddress);
        // 创建 ERC20 合约控制器
        const erc20Token = createERC20Controller(tokenAddress);
        if (!erc20Token) {
            throw new Error(`Failed to create ERC20 controller for token: ${tokenAddress}`);
        }

        try {
            // 获取代币精度（默认18位）
            let decimals = 18;
            try {
                decimals = await erc20Token.decimals();
            } catch (error) {
                console.warn('[Payment] Failed to get token decimals, using default 18');
            }
            // 转换金额
            const amount = ethers.utils.parseUnits(payment.amount, decimals);
            // 检查余额
            const balance = await erc20Token.balanceOf(WalletContext.Address);
            if (balance.lt(amount)) {
                throw new Error(`Insufficient balance. Required: ${payment.amount}, Available: ${ethers.utils.formatUnits(balance, decimals)}`);
            }
            // 获取 signer
            const provider = new providers.Web3Provider(WalletContext.Provider);
            // 构建交易数据（不发送）
            console.log(`[Payment] Building transaction for ${payment.amount} tokens transfer to ${payment.destination}`);
            const txData = await erc20Token.populateTransaction.transfer(payment.destination, amount);
            // 获取 nonce、gasPrice 等信息
            const [gasPrice, nonce] = await Promise.all([provider.getGasPrice(), provider.getTransactionCount(WalletContext.Address, 'pending')]);
            let gasLimit = ethers.BigNumber.from('21000');
            try {
                gasLimit = await erc20Token.estimateGas.transfer(payment.destination, amount);
            } catch (err) {
                console.warn('[Payment] estimateGas failed, using default 21000');
            }
            // 构建完整的交易对象
            const unsignedTx = {
                from: WalletContext.Address,
                to: txData.to,
                data: txData.data,
                nonce: nonce,
                gasLimit: gasLimit,
                gasPrice: gasPrice,
                chainId: WalletContext.Chain?.chainId,
                value: 0,
            };
            // 签名交易（不广播）
            console.log('[Payment] Signing transaction...');
            const signature = await signMessage(unsignedTx.data || '');
            console.log('[Payment] Message signature:', signature);
            // 3️⃣ 返回数据给上层逻辑或发送后端
            // 返回序列化的交易数据（rawTxData）
            return btoa(
                JSON.stringify({
                    unsignedTx,
                    signature,
                    from: WalletContext.Address,
                })
            );
        } catch (error) {
            console.error('[Payment] ERC20 transaction signing failed:', error);
            throw new Error(`ERC20 transaction signing failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    },
};

// ==========================
// 🚀 通用支付处理函数
// ==========================
export async function handlePayment(payment: PaymentInfo): Promise<string> {
    const strategy = PaymentStrategies[payment.scheme];
    if (!strategy) throw new Error(`Unsupported payment scheme: ${payment.scheme}`);
    // 🔔 发出“开始支付”事件
    console.log('[paymentHandler] emit start', payment);
    try {
        const signedData = await strategy(payment);
        const xPaymentHeader = await buildPaymentHeader(payment, signedData);
        console.log('[Payment] Build X-PAYMENT header success:', xPaymentHeader);
        return xPaymentHeader;
    } catch (err: any) {
        throw err;
    }
}
